import React, { useEffect, useState } from "react";
import { useField } from "formik";
import { FieldRenderers, FormField } from "@avamae/formbuilder";
import { useFormikContext } from "formik";
import PhoneNumberField from "components/PhoneNumberInput/PhoneNumberInput";

type SingleSelectOption = {
  label: string;
  value: number;
};

type PhoneNumberFieldRendererProps = {
  readOnly: boolean;
  name: string;
  phoneNumberRequired: boolean;
  editVersionButDisabled?: boolean;
};

export const PhoneNumberFieldRenderer: React.FC<PhoneNumberFieldRendererProps> = ({
  readOnly,
  name,
  phoneNumberRequired,
  editVersionButDisabled,
}) => {
  const [field] = useField<any>(name);

  const [userInteracted, setUserInteracted] = useState<boolean>(false);
  const [fieldValue, setFieldValue] = useState<string | null>(null);
  const [relevantFormikError, setRelevantFormikError] = useState<
    string | undefined
  >(undefined);

  const formik = useFormikContext<{ [key: string]: string }>();
  const formikErrors = formik.errors;

  useEffect(() => {
    if (formikErrors && formikErrors.hasOwnProperty(field.name)) {
      const error = formikErrors[field.name as keyof typeof formikErrors];

      setRelevantFormikError(error);
    } else {
      setRelevantFormikError(undefined);
    }
  }, [formikErrors]);

  useEffect(() => {
    if (field && typeof field === "object" && field.hasOwnProperty("value")) {
      setFieldValue(field.value);
    }
  }, [field]);

  const onChange = (v: any) => {
    setFieldValue(v);

    formik.setFieldValue(field.name, v ?? null);
  };

  return (
    <div
      className={`PhoneNumberFieldRenderer ${
        (userInteracted || formik.submitCount > 0) && relevantFormikError
          ? "Error"
          : ""
      } ${editVersionButDisabled ? "EditDisabled" : ""}`}
      onBlur={() => setUserInteracted(true)}
    >
      {readOnly ? (
        <div className="Value">{fieldValue ? fieldValue : ""}</div>
      ) : (
        <PhoneNumberField
          value={fieldValue ? fieldValue : ""}
          onChange={onChange}
          formikFieldName="phoneNumber"
          formikErrors={formik.errors}
          setFormikErrors={formik.setErrors}
          showErrorsAfterBlur={false}
          showErrorsAfterSubmit={true}
          submitted={formik.submitCount > 0}
          hideErrorMessages={true}
          disabled={readOnly}
          noPhoneNumberError={"Please provide a phone number"}
          phoneNumberRequired={phoneNumberRequired}
        />
      )}

      {(userInteracted || formik.submitCount > 0) &&
        relevantFormikError &&
        relevantFormikError.split(",").map(item => {
          return <div className="ErrorLabel">{item}</div>;
        })}
    </div>
  );
};

export const phoneNumberFieldRenderer = (
  readOnly: boolean,
  phoneNumberRequired: boolean = true,
  editVersionButDisabled?: boolean
) =>
  function phoneNumberFieldRenderer<T>(
    i: React.ReactText,
    field: FormField<T>
  ) {
    return (
      <div key={i} className="FormBox FormSideBySide">
        <div className="FormLabel">
          <label>{field.label}</label>
        </div>
        <div className="FormField">
          <PhoneNumberFieldRenderer
            readOnly={readOnly}
            name={String(field.name)}
            phoneNumberRequired={phoneNumberRequired}
            editVersionButDisabled={editVersionButDisabled}
          />
        </div>
      </div>
    );
  };
